import { useMemo, useState } from 'react';
import { Users, Plus, MoreHorizontal, Link2, Copy, Check, Lock } from 'lucide-react';
import { Toolbar } from '@vnetwork/ui/layout';
import { DataTable, StatusPill, EmptyState, type ColumnDef } from '@vnetwork/ui/data';
import {
  Button,
  Badge,
  Input,
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardBody,
  Skeleton,
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  IconButton,
  Explainer,
} from '@vnetwork/ui/primitives';
import { ConfirmDialog, useToast } from '@vnetwork/ui/feedback';
import {
  useTeamMembers,
  useRemoveMember,
  useUpdateMember,
  useResendInvitation,
  useTeamInvitationLink,
  useGenerateInvitationLink,
  useRevokeInvitationLink,
  memberStatus,
  type Member,
} from '@/api/teams';
import { InviteMembersDialog } from './components/InviteMembersDialog';
import { MEMBER_STATUS_TONE, MEMBER_STATUS_LABEL, formatDate, invitationUrl } from './shared';

interface TeamMembersTabProps {
  teamId: string;
  isOwner: boolean;
}

function InvitationLinkCard({ teamId }: { teamId: string }) {
  const { show } = useToast();
  const { data: link, isLoading } = useTeamInvitationLink(teamId);
  const [copied, setCopied] = useState(false);
  const [confirmRevoke, setConfirmRevoke] = useState(false);

  const generate = useGenerateInvitationLink(teamId, {
    onSuccess: () => show({ tone: 'success', title: 'Invitation link generated' }),
    onError: () => show({ tone: 'danger', title: 'Could not generate link' }),
  });
  const revoke = useRevokeInvitationLink(teamId, {
    onSuccess: () => {
      show({ tone: 'success', title: 'Invitation link revoked' });
      setConfirmRevoke(false);
    },
    onError: () => show({ tone: 'danger', title: 'Could not revoke link' }),
  });

  const url = link ? invitationUrl(link.token) : '';

  async function copy() {
    if (!url) return;
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1500);
    } catch {
      show({ tone: 'danger', title: 'Could not copy link' });
    }
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Invitation link</CardTitle>
        <CardDescription>
          Anyone with this link can request to join the team. It rotates after each new member
          joins.
        </CardDescription>
      </CardHeader>
      <CardBody>
        {isLoading ? (
          <Skeleton style={{ height: 40 }} />
        ) : link ? (
          <div style={{ display: 'grid', gap: 'var(--vn-space-3)' }}>
            <div style={{ display: 'flex', gap: 'var(--vn-space-2)', alignItems: 'center' }}>
              <Input readOnly value={url} style={{ flex: 1 }} startSlot={<Link2 size={15} />} />
              <Button
                variant="secondary"
                startIcon={copied ? <Check size={15} /> : <Copy size={15} />}
                onClick={copy}
              >
                {copied ? 'Copied' : 'Copy'}
              </Button>
            </div>
            <div style={{ display: 'flex', gap: 'var(--vn-space-2)' }}>
              <Button
                variant="secondary"
                size="sm"
                disabled={generate.isPending}
                onClick={() => generate.mutate()}
              >
                {generate.isPending ? 'Regenerating…' : 'Regenerate'}
              </Button>
              <Button variant="danger" size="sm" onClick={() => setConfirmRevoke(true)}>
                Revoke
              </Button>
            </div>
          </div>
        ) : (
          <Button
            variant="secondary"
            startIcon={<Link2 size={15} />}
            disabled={generate.isPending}
            onClick={() => generate.mutate()}
          >
            {generate.isPending ? 'Generating…' : 'Generate invitation link'}
          </Button>
        )}
      </CardBody>

      <ConfirmDialog
        open={confirmRevoke}
        onOpenChange={setConfirmRevoke}
        title="Revoke invitation link?"
        description="The current link will stop working immediately. You can generate a new one later."
        confirmLabel="Revoke"
        destructive
        loading={revoke.isPending}
        onConfirm={() => revoke.mutate()}
      />
    </Card>
  );
}

export function TeamMembersTab({ teamId, isOwner }: TeamMembersTabProps) {
  const { show } = useToast();
  const { data: members = [], isLoading } = useTeamMembers(teamId);

  const [inviteOpen, setInviteOpen] = useState(false);
  const [removing, setRemoving] = useState<Member | null>(null);
  const [restricting, setRestricting] = useState<Member | null>(null);

  const remove = useRemoveMember(teamId, {
    onSuccess: () => {
      show({ tone: 'success', title: 'Member removed' });
      setRemoving(null);
    },
    onError: () => show({ tone: 'danger', title: 'Could not remove member' }),
  });
  const update = useUpdateMember(teamId, {
    onSuccess: (m) => {
      show({
        tone: 'success',
        title: m.restricted ? 'Access restricted' : 'Restriction removed',
      });
      setRestricting(null);
    },
    onError: () => show({ tone: 'danger', title: 'Could not update member' }),
  });
  const resend = useResendInvitation(teamId, {
    onSuccess: () => show({ tone: 'success', title: 'Invitation resent' }),
    onError: () => show({ tone: 'danger', title: 'Could not resend invitation' }),
  });

  async function copyInviteLink(m: Member) {
    if (!m.invite_token) return;
    try {
      await navigator.clipboard.writeText(invitationUrl(m.invite_token));
      show({ tone: 'success', title: 'Invite link copied' });
    } catch {
      show({ tone: 'danger', title: 'Could not copy link' });
    }
  }

  const columns = useMemo<ColumnDef<Member, unknown>[]>(() => {
    const cols: ColumnDef<Member, unknown>[] = [
      {
        id: 'email',
        header: 'Member',
        accessorKey: 'email',
        size: 280,
        cell: ({ row }) => (
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 'var(--vn-space-2)', fontWeight: 500 }}>
            <Users size={14} color="var(--vn-fg-muted)" />
            {row.original.email}
          </span>
        ),
      },
      {
        id: 'status',
        header: 'Status',
        enableSorting: false,
        cell: ({ row }) => {
          const s = memberStatus(row.original);
          return <StatusPill tone={MEMBER_STATUS_TONE[s]}>{MEMBER_STATUS_LABEL[s]}</StatusPill>;
        },
      },
      {
        id: 'roles',
        header: 'Roles',
        enableSorting: false,
        cell: ({ row }) => {
          const roles = row.original.roles?.length ? row.original.roles : ['member'];
          return (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--vn-space-1)' }}>
              {roles.map((r) => (
                <Badge key={r} tone="neutral" appearance="subtle" size="sm">
                  {r}
                </Badge>
              ))}
              {row.original.restricted && (
                <Badge tone="warning" appearance="subtle" size="sm" startIcon={<Lock />}>
                  Restricted
                </Badge>
              )}
            </div>
          );
        },
      },
      {
        id: 'joined',
        header: 'Joined',
        accessorKey: 'joined_at',
        cell: ({ row }) => {
          const m = row.original;
          if (m.pending) {
            return (
              <span style={{ color: 'var(--vn-fg-muted)' }}>
                Invited {formatDate(m.invited_at)}
              </span>
            );
          }
          return (
            <span style={{ color: 'var(--vn-fg-muted)' }}>
              {formatDate(m.joined_at ?? m.created_at)}
            </span>
          );
        },
      },
    ];

    if (isOwner) {
      cols.push({
        id: 'actions',
        header: '',
        enableSorting: false,
        cell: ({ row }) => {
          const m = row.original;
          return (
            <div
              style={{
                display: 'flex',
                justifyContent: 'flex-end',
                alignItems: 'center',
                gap: 'var(--vn-space-1)',
              }}
            >
              {m.pending && m.invite_token && (
                <IconButton
                  variant="ghost"
                  size="sm"
                  aria-label="Copy invite link"
                  title="Copy invite link"
                  onClick={() => copyInviteLink(m)}
                >
                  <Link2 size={16} />
                </IconButton>
              )}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <IconButton variant="ghost" size="sm" aria-label="Member actions">
                    <MoreHorizontal size={16} />
                  </IconButton>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  {m.pending && (
                    <DropdownMenuItem onSelect={() => resend.mutate(m.id)}>
                      Resend invitation
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuItem onSelect={() => setRestricting(m)}>
                    {m.restricted ? 'Remove restriction' : 'Restrict access'}
                  </DropdownMenuItem>
                  <DropdownMenuItem tone="danger" onSelect={() => setRemoving(m)}>
                    Remove member
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          );
        },
      });
    }

    return cols;
  }, [isOwner, resend]);

  return (
    <div style={{ display: 'grid', gap: 'var(--vn-space-4)' }}>
      {isOwner && (
        <Explainer
          title="How member access works"
          description="Full access by default — or restrict a member to only what you share"
        >
          <ul
            style={{
              display: 'grid',
              gap: 'var(--vn-space-3)',
              margin: 0,
              paddingLeft: 'var(--vn-space-4)',
              fontSize: 'var(--vn-font-size-sm)',
              color: 'var(--vn-fg-muted)',
              lineHeight: 'var(--vn-line-height-normal)',
            }}
          >
            <li>
              By default a member has <strong>full access</strong> to your organization's resources.
            </li>
            <li>
              Turn on <strong>Restricted</strong> (member menu → “Restrict access”) to flip them to{' '}
              <strong>deny-by-default</strong> — they then see only the resources explicitly shared
              with them.
            </li>
            <li>
              Grant those resources in the <strong>Permissions</strong> tab. A restricted member
              sees exactly what's shared — nothing from their own organization.
            </li>
            <li style={{ color: 'var(--vn-warning-text)' }}>
              Heads-up: sharing a <strong>whole service</strong> overrides specific-resource shares.
              To truly limit a member, share specific resources only.
            </li>
          </ul>
        </Explainer>
      )}

      {isOwner && <InvitationLinkCard teamId={teamId} />}

      {isOwner && (
        <Toolbar
          end={
            <Button
              variant="secondary"
              size="sm"
              startIcon={<Plus size={15} />}
              onClick={() => setInviteOpen(true)}
            >
              Invite members
            </Button>
          }
        />
      )}

      {isLoading ? (
        <div style={{ display: 'grid', gap: 'var(--vn-space-3)' }}>
          {Array.from({ length: 3 }).map((_, i) => (
            <Skeleton key={i} style={{ height: 44 }} />
          ))}
        </div>
      ) : members.length === 0 ? (
        <EmptyState
          icon={<Users size={28} />}
          title="No members yet"
          description={
            isOwner
              ? 'Invite people by email or share the invitation link above.'
              : 'This team has no members yet.'
          }
        />
      ) : (
        <DataTable columns={columns} data={members} getRowId={(r) => r.id} />
      )}

      <InviteMembersDialog open={inviteOpen} onOpenChange={setInviteOpen} teamId={teamId} />

      <ConfirmDialog
        open={!!removing}
        onOpenChange={(o) => !o && setRemoving(null)}
        title="Remove member?"
        description={`“${removing?.email}” will lose access to this team.`}
        confirmLabel="Remove"
        destructive
        loading={remove.isPending}
        onConfirm={() => removing && remove.mutate(removing.id)}
      />

      <ConfirmDialog
        open={!!restricting}
        onOpenChange={(o) => !o && setRestricting(null)}
        title={restricting?.restricted ? 'Remove restriction?' : 'Restrict access?'}
        description={
          restricting?.restricted
            ? `“${restricting?.email}” will regain full access to your organization's resources.`
            : `“${restricting?.email}” will only see resources explicitly shared with them — their own organization's resources stay hidden.`
        }
        confirmLabel={restricting?.restricted ? 'Remove restriction' : 'Restrict'}
        loading={update.isPending}
        onConfirm={() =>
          restricting &&
          update.mutate({
            memberId: restricting.id,
            data: { restricted: !restricting.restricted },
          })
        }
      />
    </div>
  );
}
